import { createClient } from '@supabase/supabase-js';
import type { Tela, Midia, Playlist, PlaylistItem, Empresa, Anunciante, UserEmpresa } from '../types';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error(
    'Variáveis de ambiente do Supabase não encontradas.\n' +
    'Copie o arquivo .env.example para .env e preencha as credenciais.'
  );
}

// Tipagem do banco para autocompletar nas queries.
// Nota: omitir Views/Functions mantém Schema = any no cliente Supabase v2,
// o que é intencional para este MVP — evita conflito com GenericTable constraint.
export type Database = {
  public: {
    Tables: {
      telas: {
        Row:    Tela;
        Insert: Omit<Tela, 'id' | 'created_at'>;
        Update: Partial<Omit<Tela, 'id' | 'created_at'>>;
      };
      midias: {
        Row:    Midia;
        Insert: Omit<Midia, 'id' | 'created_at'>;
        Update: Partial<Omit<Midia, 'id' | 'created_at'>>;
      };
      playlists: {
        Row:    Playlist;
        Insert: Omit<Playlist, 'id' | 'created_at'>;
        Update: Partial<Omit<Playlist, 'id' | 'created_at'>>;
      };
      playlist_itens: {
        Row:    PlaylistItem;
        Insert: Omit<PlaylistItem, 'id' | 'created_at'>;
        Update: Partial<Omit<PlaylistItem, 'id' | 'created_at'>>;
      };
      empresas: {
        Row:    Empresa;
        Insert: Omit<Empresa, 'id' | 'criado_em'>;
        Update: Partial<Omit<Empresa, 'id' | 'criado_em'>>;
      };
      anunciantes: {
        Row:    Anunciante;
        Insert: Omit<Anunciante, 'id' | 'criado_em'>;
        Update: Partial<Omit<Anunciante, 'id' | 'criado_em'>>;
      };
      user_empresas: {
        Row:    UserEmpresa;
        Insert: UserEmpresa;
        Update: Partial<UserEmpresa>;
      };
    };
  };
};

// `worker: true` roda o heartbeat do Realtime num Web Worker — em aba/app
// em background (mobile PWA, navegador in-app), o timer do heartbeat na
// main thread é throttled/suspenso pelo browser, e a conexão morre sem
// avisar (nenhum evento de erro, só para de entregar postgres_changes).
// `heartbeatCallback` detecta esse estado e força reconexão proativa —
// padrão recomendado pela Supabase para "silent disconnections in
// backgrounded applications". Sozinho isso só restaura o socket; o
// catch-up dos eventos perdidos enquanto ficou desconectado ainda depende
// do refetch em `visibilitychange`/`pageshow`/`focus` (ver useJukebox e o
// watchdog WS do Player) — Realtime não faz replay de CDC perdido.
export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  realtime: {
    params: {
      eventsPerSecond: 10,
    },
    // jsdom (Vitest) não implementa Worker — sem o guard, o import quebra
    // qualquer teste que toque neste módulo, mesmo indiretamente.
    worker: typeof Worker !== 'undefined',
    heartbeatCallback: (status) => {
      if (status === 'disconnected' || status === 'timeout') {
        supabase.realtime.connect();
      }
    },
  },
});
